// API client for the AC Analyst surface — A5.
//
// Talks to the coframe-runtime /analyst/* routes (which live on
// localhost:8001 by default; proxied as /runtime/* in dev). Mirrors
// the Pydantic models in
// packages/coframe-runtime/src/coframe/runtime/http/analyst_routes.py.
//
// The author-side client (./client.ts) stays focused on /sessions
// (workbench sessions) — the analyst client is intentionally a
// separate object since (a) it speaks to a different process and
// (b) the surfaces stay independently swappable.

import { ApiError } from "./client";

// ─────────────────────────────────────────────────────────────────────
// Shared types — mirror analyst_routes.py
// ─────────────────────────────────────────────────────────────────────

/** The 11 artifact kinds per design doc §4.2. The UI dispatches
 *  renderers off this string. */
export type ArtifactKind =
  | "narration"
  | "frame"
  | "chart"
  | "qm_card"
  | "structure"
  | "comparison"
  | "simulation"
  | "followups"
  | "refusal"
  | "provenance"
  | "clarification";

export interface ArtifactView {
  id: string;
  kind: ArtifactKind;
  payload: Record<string, unknown>;
  provenance: Record<string, unknown>;
  pinned: boolean;
  created_at: number;
}

export interface CreateSessionRequest {
  installation_id: string;
  ac_name?: string;
  /** Optional bounded-loop overrides per design doc §8.2. */
  max_turns_per_intent?: number;
  stuck_threshold?: number;
  dedup_window?: number;
  max_parallel_tools?: number;
}

export interface CreateSessionResponse {
  session_id: string;
  ac_name: string;
  installation_id: string;
  prompt_version: string;
  model: string;
}

export interface SendMessageResponse {
  session_id: string;
  text: string;
  artifacts: ArtifactView[];
  turns_used: number;
  budget_exceeded: boolean;
  stuck_warning_fired: boolean;
}

export interface SessionView {
  session_id: string;
  ac_name: string;
  installation_id: string;
  intent_index: number;
  turn_index: number;
  artifact_count: number;
  /** Most-recent N artifacts; full list at /artifacts. */
  artifacts: ArtifactView[];
}

export interface TraceEventView {
  t: number;
  kind: string;
  payload: Record<string, unknown>;
}

export interface TraceView {
  session_id: string;
  ac_name: string;
  prompt_version: string;
  model: string;
  started_at: number;
  events: TraceEventView[];
  format_version: string;
}

export interface ExportView {
  session_id: string;
  ac_name: string;
  installation_id: string;
  prompt_version: string;
  model: string;
  messages: Array<Record<string, unknown>>;
  artifacts: ArtifactView[];
  trace: TraceView;
}

// ─────────────────────────────────────────────────────────────────────
// Client
// ─────────────────────────────────────────────────────────────────────

export interface AnalystClientOptions {
  /** Base URL for the runtime API. Defaults to "/runtime" (Vite proxy
   *  in dev). Override for SSR / production / cross-origin deploys. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export class AnalystApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: AnalystClientOptions = {}) {
    // Resolution order: explicit option > Vite build-time env var >
    // dev-proxy default. Production builds set VITE_RUNTIME_BASE_URL
    // to "" so calls go to the same origin (the Fly app serving the
    // runtime FastAPI directly). Dev keeps "/runtime" so Vite proxies
    // to localhost:8001.
    const envBase = (import.meta.env?.VITE_RUNTIME_BASE_URL as string | undefined);
    this.baseUrl = options.baseUrl ?? envBase ?? "/runtime";
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  // ── Sessions ─────────────────────────────────────────────────────

  async createSession(req: CreateSessionRequest): Promise<CreateSessionResponse> {
    return this.request<CreateSessionResponse>(
      "POST",
      "/analyst/sessions",
      req,
    );
  }

  async sendMessage(
    sessionId: string,
    text: string,
  ): Promise<SendMessageResponse> {
    return this.request<SendMessageResponse>(
      "POST",
      `/analyst/sessions/${encodeURIComponent(sessionId)}/messages`,
      { text },
    );
  }

  async getSession(sessionId: string): Promise<SessionView> {
    return this.request<SessionView>(
      "GET",
      `/analyst/sessions/${encodeURIComponent(sessionId)}`,
    );
  }

  async listArtifacts(sessionId: string): Promise<ArtifactView[]> {
    return this.request<ArtifactView[]>(
      "GET",
      `/analyst/sessions/${encodeURIComponent(sessionId)}/artifacts`,
    );
  }

  async getArtifact(
    sessionId: string,
    artifactId: string,
  ): Promise<ArtifactView> {
    return this.request<ArtifactView>(
      "GET",
      `/analyst/sessions/${encodeURIComponent(sessionId)}` +
        `/artifacts/${encodeURIComponent(artifactId)}`,
    );
  }

  async getTrace(sessionId: string): Promise<TraceView> {
    return this.request<TraceView>(
      "GET",
      `/analyst/sessions/${encodeURIComponent(sessionId)}/trace`,
    );
  }

  async exportSession(sessionId: string): Promise<ExportView> {
    return this.request<ExportView>(
      "GET",
      `/analyst/sessions/${encodeURIComponent(sessionId)}/export`,
    );
  }

  // ── Internals ────────────────────────────────────────────────────

  private async request<T>(
    method: "GET" | "POST",
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const init: RequestInit = {
      method,
      headers:
        body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    };
    const response = await this.fetchImpl(url, init);
    let payload: unknown = undefined;
    try {
      payload = await response.json();
    } catch {
      // Empty body — fine for some error paths.
    }
    if (!response.ok) {
      const detail =
        typeof payload === "object" && payload !== null && "detail" in payload
          ? extractDetailMessage((payload as { detail: unknown }).detail)
          : response.statusText;
      throw new ApiError(response.status, detail, payload);
    }
    return payload as T;
  }
}

function extractDetailMessage(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && detail !== null) {
    const d = detail as Record<string, unknown>;
    if (typeof d.message === "string") return d.message;
    if (typeof d.error === "string") return d.error;
    return JSON.stringify(detail);
  }
  return String(detail);
}
